import React from 'react';
import useNow from '../hooks/useNow';
import { clamp, formatAmount, formatCountdown, levelRange, levelValue, parseServerTime } from '../utils/missionMath';
import { COIN_ICON, DNA_ICON, resolveAssetUrl } from '../utils/lairAssets';
import { ErrorBox } from './MissionsTab';
import { LockIcon } from './LairIcons';
import { useTranslation } from 'react-i18next';
import { buildingName } from '../utils/lairAssets';

const intervalText = (t, minutes) => {
  const m = Number(minutes) || 0;
  if (m % 60 === 0) return t('time.hours_short', '{{n}} h', { n: m / 60 });
  return m > 60
    ? t('time.hours_minutes_short', '{{h}} h {{m}} min', { h: Math.floor(m / 60), m: m % 60 })
    : t('time.minutes_short', '{{n}} min', { n: m });
};

function Price({ coins, dna, haveCoins, haveDna }) {
  const { t } = useTranslation();
  return (
    <span className="lair-farm__price">
      <span className={haveCoins < coins ? 'lair-farm__short' : ''}>
        <img src={COIN_ICON} alt={t('common.coins', 'Coins')} />{formatAmount(coins)}
      </span>
      {dna > 0 && (
        <span className={haveDna < dna ? 'lair-farm__short' : ''}>
          <img src={DNA_ICON} alt={t('lair.dna', 'DNA')} />{formatAmount(dna)}
        </span>
      )}
    </span>
  );
}

/**
 * One farm (v1.47.0): locked → unlock for coins + DNA; open → put a hero inside; working → countdown to the
 * reward (ad finishes it now) → Claim, the hero leaves the farm. The reward depends on the hero level.
 */
function FarmCard({
  farm, coins, dna, serverNow, busy, anyBusy, adBusy, flash, onUnlock, onPickHero, onRemoveHero, onClaim, onWatchAd,
}) {
  const { t } = useTranslation();
  const hero = farm.hero;
  const readyAt = parseServerTime(farm.readyAt);
  const intervalMs = (Number(farm.intervalMinutes) || 120) * 60000;
  const left = Number.isFinite(readyAt) ? readyAt - serverNow : 0;
  const ready = hero && (farm.ready || left <= 0);
  const pct = ready ? 100 : clamp(((intervalMs - left) / intervalMs) * 100, 0, 100);
  const reward = Number(farm.rewardCoins) || levelValue(farm.levelCoins, hero?.level);

  return (
    <div className={`lair-farm${farm.unlocked ? '' : ' lair-farm--locked'}${ready ? ' lair-farm--ready' : ''}${flash ? ' lair-farm--flash' : ''}`}>
      <div className="lair-farm__head">
        <div className="lair-farm__icon">
          <img src={resolveAssetUrl(farm.imageUrl) || '/lair/bunker.svg'} alt="" />
          {!farm.unlocked && <LockIcon className="lair-glyph lair-farm__lock" />}
        </div>
        <div className="lair-farm__info">
          <div className="lair-farm__name">{buildingName(farm, t)}</div>
          <div className="lair-farm__rate">
            <img src={COIN_ICON} alt="" />
            {hero ? formatAmount(reward) : levelRange(farm.levelCoins)}
            <span className="lair-muted"> / {intervalText(t, farm.intervalMinutes)}</span>
          </div>
        </div>
        {hero && (
          <button
            type="button"
            className="lair-farm__hero"
            disabled={anyBusy}
            onClick={() => onRemoveHero(farm)}
            aria-label={t('lair.farm.take_out', 'Take the hero out')}
            title={t('lair.farm.take_out', 'Take the hero out')}
          >
            <img src={resolveAssetUrl(hero.headImageUrl) || '/inventory/character.png'} alt="" />
            <span className="lair-farm__lvl">{t('lair.level_short', 'Lv {{n}}', { n: hero.level })}</span>
            <span className="lair-farm__x">×</span>
          </button>
        )}
      </div>

      {!farm.unlocked ? (
        <button
          type="button"
          className="app-btn app-btn--green app-btn--sm lair-farm__btn"
          disabled={!farm.canUnlock || anyBusy}
          onClick={() => onUnlock(farm)}
        >
          {busy ? '…' : <>{t('lair.farm.unlock', 'Unlock')} <Price coins={farm.unlockCoins} dna={farm.unlockDna} haveCoins={coins} haveDna={dna} /></>}
        </button>
      ) : !hero ? (
        <button
          type="button"
          className="app-btn app-btn--purple app-btn--sm lair-farm__btn"
          disabled={anyBusy}
          onClick={() => onPickHero(farm)}
        >
          {t('lair.farm.put_hero', 'Put a hero to work')}
        </button>
      ) : ready ? (
        <button
          type="button"
          className="app-btn app-btn--yellow app-btn--sm lair-farm__btn"
          disabled={anyBusy}
          onClick={() => onClaim(farm)}
        >
          {busy ? t('lair.claiming', 'Claiming…') : <>{t('common.claim', 'Claim')} <img className="app-btn__icon" src={COIN_ICON} alt="" />{formatAmount(reward)}</>}
        </button>
      ) : (
        <div className="lair-farm__work">
          <div className="lair-progress">
            <div className="lair-progress__fill" style={{ width: `${pct}%` }} />
            <span className="lair-progress__text">{formatCountdown(left)}</span>
          </div>
          <button
            type="button"
            className="app-btn app-btn--blue app-btn--sm lair-farm__ad"
            disabled={adBusy}
            onClick={() => onWatchAd(farm)}
          >
            <img className="app-btn__icon" src="/adv_icon.svg" alt="" />{adBusy ? '…' : t('lair.farm.now', 'Now')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function LairTab({
  data, loaded, error, offsetMs = 0, busyId, adBusyId, lastClaim,
  onUnlock, onPickHero, onRemoveHero, onClaim, onWatchAd, onRetry,
}) {
  const { t } = useTranslation();
  const farms = data?.buildings || [];
  const working = farms.some((f) => f.hero);
  const now = useNow(1000, working);

  if (!loaded && !data) {
    return error
      ? <ErrorBox error={error} onRetry={onRetry} />
      : <div className="lair-loading"><div className="lair-spinner" /></div>;
  }

  const serverNow = now + (Number(offsetMs) || 0);
  const flashId = lastClaim && Date.now() - lastClaim.at < 1500 ? lastClaim.buildingId : null;

  return (
    <div className="lair-tab">
      <div className="lair-sheet-hint lair-farms__hint">
        {t('lair.farm.hint', 'Put a hero to work: the higher its level, the more coins every {{interval}}.', {
          interval: intervalText(t, farms[0]?.intervalMinutes || 120),
        })}
      </div>
      {farms.length === 0 ? (
        <div className="lair-empty">{t('lair.farm.none', 'No farms yet')}</div>
      ) : (
        <div className="lair-farms">
          {farms.map((f) => (
            <FarmCard
              key={`${f.id}-${flashId === f.id ? lastClaim.at : ''}`}
              farm={f}
              coins={Number(data?.coins) || 0}
              dna={Number(data?.dna) || 0}
              serverNow={serverNow}
              busy={busyId === f.id}
              anyBusy={!!busyId}
              adBusy={adBusyId === f.id}
              flash={flashId === f.id}
              onUnlock={onUnlock}
              onPickHero={onPickHero}
              onRemoveHero={onRemoveHero}
              onClaim={onClaim}
              onWatchAd={onWatchAd}
            />
          ))}
        </div>
      )}
    </div>
  );
}
